import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Clock,
  Award,
  Users,
  ArrowRight,
  Code,
  Palette,
  Briefcase,
  MessageCircle,
  Plus,
  Loader,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import styles from "./Assessments.module.css";

const Assessments = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("available");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All");
  const [assessments, setAssessments] = useState([]);
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Handle URL query parameters
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const tabParam = searchParams.get("tab");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    if (tabParam === "history") {
      setActiveTab("history");
    }
  }, [searchParams]);

  // Fetch assessments and skills from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch assessments, skills, and assessment history in parallel
        const [assessmentsResponse, skillsResponse, historyResponse] =
          await Promise.all([
            apiService.assessments.getAllAssessments(),
            apiService.assessments.getSkills(),
            apiService.progress.getDashboard(),
          ]);

        if (assessmentsResponse && !assessmentsResponse.error) {
          setAssessments(assessmentsResponse);
        } else {
          throw new Error(
            assessmentsResponse?.error || "Failed to fetch assessments",
          );
        }

        if (skillsResponse && !skillsResponse.error) {
          setSkills(skillsResponse);
        } else {
          console.warn("Failed to fetch skills:", skillsResponse?.error);
          setSkills([]);
        }

        // Get all assessment attempts from dashboard data
        if (historyResponse && historyResponse.recentAssessments) {
          setAssessmentHistory(historyResponse.recentAssessments);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load assessments");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh data when window gains focus (user returns from another page)
  useEffect(() => {
    const handleFocus = () => {
      // Only refresh if we're not currently loading
      if (!loading) {
        refreshAssessments();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [loading]);

  // Manual refresh function
  const refreshAssessments = async () => {
    try {
      setRefreshing(true);
      const assessmentsResponse =
        await apiService.assessments.getAllAssessments();
      if (assessmentsResponse && !assessmentsResponse.error) {
        setAssessments(assessmentsResponse);
      }
    } catch (err) {
      console.error("Error refreshing assessments:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Get category icon based on skill category
  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case "programming":
      case "backend":
      case "frontend":
        return Code;
      case "design":
        return Palette;
      case "management":
        return Briefcase;
      case "soft skills":
        return MessageCircle;
      default:
        return Award;
    }
  };

  // Get category color based on skill category
  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case "programming":
      case "backend":
      case "frontend":
        return "#3b82f6";
      case "design":
        return "#8b5cf6";
      case "management":
        return "#f59e0b";
      case "soft skills":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  // Get unique categories from skills
  const categories = [
    "All",
    ...new Set(skills.map((skill) => skill.category).filter(Boolean)),
  ];
  const difficulties = ["All", "beginner", "intermediate", "advanced"];

  const filteredAssessments = assessments.filter((assessment) => {
    const matchesSearch =
      assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      assessment.skillName?.toLowerCase().includes(searchTerm.toLowerCase());

    const skillCategory = skills.find(
      (skill) => skill.id === assessment.skillId,
    )?.category;
    const matchesCategory =
      selectedCategory === "All" || skillCategory === selectedCategory;

    const matchesDifficulty =
      selectedDifficulty === "All" ||
      assessment.difficultyLevel === selectedDifficulty;

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner":
        return "#10b981";
      case "intermediate":
        return "#f59e0b";
      case "advanced":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className={styles.assessmentsPage}>
        <div className={styles.assessmentsContainer}>
          <div className={styles.loadingState}>
            <Loader size={48} className={styles.spinner} />
            <h3>Loading assessments...</h3>
            <p>Please wait while we fetch the latest assessments</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.assessmentsPage}>
        <div className={styles.assessmentsContainer}>
          <div className={styles.errorState}>
            <AlertCircle size={48} />
            <h3>Failed to load assessments</h3>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.assessmentsPage}>
      <div className={styles.assessmentsContainer}>
        <div className={styles.assessmentsHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              <h1>Skill Assessments</h1>
              <p>
                Test your knowledge and track your progress across different
                skill areas
              </p>
            </div>
            {user?.role === "admin" && (
              <div className={styles.headerActions}>
                <button
                  className={styles.refreshBtn}
                  onClick={refreshAssessments}
                  disabled={refreshing}
                  title="Refresh assessments"
                >
                  <RefreshCw
                    size={20}
                    className={refreshing ? "spinning" : ""}
                  />
                </button>
                <Link
                  to="/admin/create-assessment"
                  className={styles.createAssessmentBtn}
                >
                  <Plus size={20} />
                  Create Assessment
                </Link>
              </div>
            )}
          </div>

          <div className={styles.assessmentsStats}>
            <div className={styles.statItem}>
              <Award size={20} />
              <span>{assessments.length} Assessments</span>
            </div>
            <div className={styles.statItem}>
              <Users size={20} />
              <span>10+ Completions</span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className={styles.tabNavigation}>
            <button
              className={`${styles.tabButton} ${activeTab === "available" ? styles.active : ""}`}
              onClick={() => setActiveTab("available")}
            >
              Available Assessments
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === "history" ? styles.active : ""}`}
              onClick={() => setActiveTab("history")}
            >
              Assessment History
            </button>
          </div>
        </div>

        {activeTab === "available" ? (
          <>
            <div className={styles.assessmentsFilters}>
              <div className={styles.searchBar}>
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className={styles.filterControls}>
                <div className={styles.filterGroup}>
                  <label>Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setSelectedCategory(newCategory);
                      // Update URL parameters
                      if (newCategory === "All") {
                        searchParams.delete("category");
                      } else {
                        searchParams.set("category", newCategory);
                      }
                      setSearchParams(searchParams);
                    }}
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label>Difficulty</label>
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                  >
                    {difficulties.map((difficulty) => (
                      <option key={difficulty} value={difficulty}>
                        {difficulty}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.assessmentsResults}>
              <div className={styles.resultsHeader}>
                <span>{filteredAssessments.length} assessments found</span>
              </div>

              <div className={styles.assessmentsGrid}>
                {filteredAssessments.map((assessment) => {
                  const skillCategory = skills.find(
                    (skill) => skill.id === assessment.skillId,
                  )?.category;
                  const IconComponent = getCategoryIcon(skillCategory);
                  const categoryColor = getCategoryColor(skillCategory);

                  return (
                    <div key={assessment.id} className={styles.assessmentCard}>
                      <div className={styles.cardHeader}>
                        <div
                          className={styles.assessmentIcon}
                          style={{
                            backgroundColor: `${categoryColor}20`,
                            color: categoryColor,
                          }}
                        >
                          <IconComponent size={24} />
                        </div>
                        <div className={styles.assessmentMeta}>
                          <span className={styles.category}>
                            {skillCategory || assessment.skillName}
                          </span>
                        </div>
                      </div>

                      <div className={styles.cardContent}>
                        <h3>{assessment.title}</h3>
                        <p>
                          {assessment.description ||
                            "Test your knowledge and skills"}
                        </p>

                        <div className={styles.assessmentDetails}>
                          <div className={styles.detail}>
                            <Clock size={16} />
                            <span>
                              {assessment.timeLimit
                                ? Math.round(assessment.timeLimit / 60)
                                : 30}{" "}
                              min
                            </span>
                          </div>
                          <div className={styles.detail}>
                            <Award size={16} />
                            <span>
                              {assessment.questionCount || 0} questions
                            </span>
                          </div>
                          <div
                            className={styles.difficultyBadge}
                            style={{
                              backgroundColor: getDifficultyColor(
                                assessment.difficultyLevel,
                              ),
                            }}
                          >
                            {assessment.difficultyLevel || "Beginner"}
                          </div>
                        </div>

                        <div className={styles.assessmentTags}>
                          <span className={styles.tag}>{assessment.type}</span>
                          <span className={styles.tag}>
                            Max Score: {assessment.maxScore}
                          </span>
                          <span className={styles.tag}>
                            Pass: {assessment.passingScore}%
                          </span>
                        </div>
                      </div>

                      <div className={styles.cardFooter}>
                        <Link
                          to={`/assessment/${assessment.id}`}
                          className={styles.startBtn}
                        >
                          Start Assessment
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredAssessments.length === 0 && (
                <div className={styles.noResults}>
                  <div className={styles.noResultsIcon}>
                    <Search size={48} />
                  </div>
                  <h3>No assessments found</h3>
                  <p>Try adjusting your search criteria or filters</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <h2>Your Assessment History</h2>
              <p>View all your completed assessments sorted by most recent</p>
            </div>

            {assessmentHistory.length > 0 ? (
              <div className={styles.historyList}>
                {assessmentHistory.map((attempt, index) => {
                  const skillCategory = skills.find(
                    (skill) => skill.name === attempt.skillName,
                  )?.category;
                  const IconComponent = getCategoryIcon(skillCategory);
                  const categoryColor = getCategoryColor(skillCategory);
                  const isPassed = attempt.status === "completed";

                  return (
                    <div key={index} className={styles.historyCard}>
                      <div className={styles.historyCardHeader}>
                        <div
                          className={styles.historyIcon}
                          style={{
                            backgroundColor: `${categoryColor}20`,
                            color: categoryColor,
                          }}
                        >
                          <IconComponent size={24} />
                        </div>
                        <div className={styles.historyInfo}>
                          <h3>{attempt.title}</h3>
                          <div className={styles.historyMeta}>
                            <span className={styles.skillBadge}>
                              {attempt.skillName}
                            </span>
                            <span className={styles.dateBadge}>
                              <Clock size={14} />
                              {new Date(attempt.completedAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className={styles.historyCardBody}>
                        <div className={styles.scoreSection}>
                          <div
                            className={`${styles.scoreCircle} ${isPassed ? styles.passed : styles.failed}`}
                          >
                            <span className={styles.scoreValue}>
                              {attempt.percentage}%
                            </span>
                          </div>
                          <div className={styles.scoreDetails}>
                            <div className={styles.scoreLabel}>
                              {isPassed ? "Passed" : "Failed"}
                            </div>
                          </div>
                        </div>

                        <Link
                          to={`/assessment-report/${attempt.id}`}
                          className={styles.viewReportBtn}
                        >
                          View Detailed Report
                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.noHistory}>
                <div className={styles.noHistoryIcon}>
                  <Award size={64} />
                </div>
                <h3>No Assessment History</h3>
                <p>You haven't completed any assessments yet</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setActiveTab("available")}
                >
                  Browse Available Assessments
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Assessments;
